import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLikedItems } from '../context/WishlistContext';
import { useProducts } from '../context/ProductsContext';
import { productService, categoryService } from '../services/api';
import ProductCard from '../components/ProductCard';
import SiteLoading from '../components/SiteLoading';
import {
  getCategorySlug,
  getCategoryDepth,
  sortMainCategoriesForDisplay,
} from '../utils/categoryUtils';
import { normalizeProductsArray } from '../utils/productResponse';
import {
  getProductCatalogKey,
  pickHotNewProducts,
  pickSaleProducts,
  resolveHomeTrendingByPdpViews,
  resolveHomeFeaturedProducts,
} from '../utils/homeProductSections';
import { placeholderDataUrl } from '../utils/placeholderImage';
import { getPrimaryProductImageUrl } from '../utils/productImages';
import './Home.css';

const HOME_PRODUCTS_CACHE_KEY = 'sheenwear_home_products_cache_v6_trending_featured';
const HOME_SECTION_PRODUCT_LIMIT = 8;
const HOME_CATEGORIES_CACHE_KEY = 'sheenwear_home_categories_cache_v1';
const HOME_CACHE_TTL_MS = 10 * 60 * 1000;

/** While finger is down, skip auto-advance so slide changes do not remount nodes mid-gesture (iOS scroll lock). */
const homeCarouselAutoPausedRef = { current: false };

/** Desktop: remount grid per slide for enter animation. Mobile (1-up): stable key — only ProductCard props update. */
function homeCarouselGridKey(groupSize, slideIndex) {
  return groupSize === 1 ? 'home-carousel-one-up' : slideIndex;
}

/** Passive swipe on touchend only — never touchmove/preventDefault (keeps vertical page scroll on iOS). */
function bindHomeCarouselSwipe(el, swipeRef, { onLeft, onRight }) {
  const minSwipeDistance = 48;
  const opts = { passive: true };
  const onStart = (e) => {
    const t = e.targetTouches[0];
    swipeRef.current = { x: t.clientX, y: t.clientY };
  };
  const onEnd = (e) => {
    const start = swipeRef.current;
    swipeRef.current = null;
    if (!start || !e.changedTouches?.length) return;
    const end = e.changedTouches[0];
    const horizontalDistance = start.x - end.clientX;
    const verticalDistance = start.y - end.clientY;
    const absH = Math.abs(horizontalDistance);
    const absV = Math.abs(verticalDistance);
    /* Horizontal slide only when intent is clearly L/R — vertical drags scroll the page */
    if (absH < minSwipeDistance || absH <= absV * 1.35) return;
    homeCarouselAutoPausedRef.current = true;
    window.setTimeout(() => {
      homeCarouselAutoPausedRef.current = false;
    }, 700);
    if (horizontalDistance > 0) onLeft();
    else onRight();
  };
  const onCancel = () => {
    swipeRef.current = null;
  };
  el.addEventListener('touchstart', onStart, opts);
  el.addEventListener('touchend', onEnd, opts);
  el.addEventListener('touchcancel', onCancel, opts);
  return () => {
    el.removeEventListener('touchstart', onStart, opts);
    el.removeEventListener('touchend', onEnd, opts);
    el.removeEventListener('touchcancel', onCancel, opts);
  };
}

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  /** Swipe start coords (passive listeners read these). */
  const heroSwipeRef = useRef(null);
  const featuredSwipeRef = useRef(null);
  const trendingSwipeRef = useRef(null);
  const hotNewSwipeRef = useRef(null);
  const saleSwipeRef = useRef(null);
  const featuredSliderRef = useRef(null);
  const trendingSliderRef = useRef(null);
  const hotNewSliderRef = useRef(null);
  const saleSliderRef = useRef(null);
  const heroSliderRef = useRef(null);

  const { getMostLikedProducts } = useLikedItems();
  const { products, loading, error } = useProducts();
  const [dynamicMostLikedProducts, setDynamicMostLikedProducts] = useState([]);

  //  FIXED: Use main products API for homepage sections
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [saleProducts, setSaleProducts] = useState([]);
  const [newArrivalProducts, setNewArrivalProducts] = useState([]);
  const [homepageLoading, setHomepageLoading] = useState(true);
  const [homepageError, setHomepageError] = useState(null);

  // Helper function to get group size based on screen width
  const getGroupSize = () => {
    return window.innerWidth <= 768 ? 1 : 4;
  };

  const [currentFeaturedGroup, setCurrentFeaturedGroup] = useState(0);
  const [featuredGroupSize, setFeaturedGroupSize] = useState(getGroupSize());

  // Trending products carousel state
  const [currentTrendingGroup, setCurrentTrendingGroup] = useState(0);
  const [trendingGroupSize, setTrendingGroupSize] = useState(getGroupSize());
  
  // HOT NEW section - Latest products by date
  const [currentHotNewGroup, setCurrentHotNewGroup] = useState(0);
  const [hotNewGroupSize, setHotNewGroupSize] = useState(getGroupSize());
  
  // SALE section - Sale products carousel
  const [currentSaleGroup, setCurrentSaleGroup] = useState(0);
  const [saleGroupSize, setSaleGroupSize] = useState(getGroupSize());
  
  // States for each card
  const [currentViewedSlideMan, setCurrentViewedSlideMan] = useState(0);
  const [currentSellingSlideMan, setCurrentSellingSlideMan] = useState(0);
  const [currentLikedSlideMan, setCurrentLikedSlideMan] = useState(0);
  const [viewedManualModeMan, setViewedManualModeMan] = useState(false);
  const [sellingManualModeMan, setSellingManualModeMan] = useState(false);
  const [likedManualModeMan, setLikedManualModeMan] = useState(false);
  
  // Swipe handlers for each card
  const [viewedTouchStartMan, setViewedTouchStartMan] = useState(null);
  const [viewedTouchEndMan, setViewedTouchEndMan] = useState(null);
  const [sellingTouchStartMan, setSellingTouchStartMan] = useState(null);
  const [sellingTouchEndMan, setSellingTouchEndMan] = useState(null);
  const [likedTouchStartMan, setLikedTouchStartMan] = useState(null);
  const [likedTouchEndMan, setLikedTouchEndMan] = useState(null);

  //  FIXED: Get latest products using main products API - Define before useEffect hooks
  const getLatestProducts = () => {
    const arr = Array.isArray(newArrivalProducts) ? newArrivalProducts : [];
    return [...arr]
      .sort((a, b) => new Date(b.datePosted || '01724') - new Date(a.datePosted || '01724'))
      .slice(0, 8);
  };

  //  FIXED: Load homepage products using main products API with more products
  useEffect(() => {
    const loadHomepageProducts = async () => {
      const cached = readHomeCache(HOME_PRODUCTS_CACHE_KEY);
      const hasCache =
        cached &&
        Array.isArray(cached?.data?.trendingProducts) &&
        Array.isArray(cached?.data?.featuredProducts) &&
        Array.isArray(cached?.data?.saleProducts) &&
        Array.isArray(cached?.data?.newArrivalProducts) &&
        cached.data.trendingProducts.length > 0;
      const cacheFresh = hasCache && (Date.now() - (cached.ts || 0) < HOME_CACHE_TTL_MS);

      try {
        if (hasCache) {
          setTrendingProducts(cached.data.trendingProducts);
          setFeaturedProducts(cached.data.featuredProducts || []);
          setSaleProducts(cached.data.saleProducts);
          setNewArrivalProducts(cached.data.newArrivalProducts);
          setHomepageLoading(false);
        } else {
          setHomepageLoading(true);
        }
        setHomepageError(null);
        
        console.log('Loading homepage products from main products API...');

        const [catalogResponse, trendingByViewsResponse, featuredResponse] = await Promise.all([
          productService.getProducts({
            limit: 100,
            sort: 'createdAt',
            order: 'desc',
          }),
          productService.getTrendingByPdpViews(HOME_SECTION_PRODUCT_LIMIT),
          productService.getHomeFeaturedProducts(HOME_SECTION_PRODUCT_LIMIT),
        ]);

        const catalog = normalizeProductsArray(
          catalogResponse.products ?? catalogResponse.data ?? catalogResponse
        );

        const trending = resolveHomeTrendingByPdpViews(
          catalog,
          trendingByViewsResponse,
          HOME_SECTION_PRODUCT_LIMIT
        );
        const featured = resolveHomeFeaturedProducts(
          catalog,
          featuredResponse,
          HOME_SECTION_PRODUCT_LIMIT
        );
        const hotNew = pickHotNewProducts(catalog, HOME_SECTION_PRODUCT_LIMIT);
        const sale = pickSaleProducts(catalog, new Set(), HOME_SECTION_PRODUCT_LIMIT);

        console.log(
          ' Trending (PDP views) source:',
          trendingByViewsResponse?.source || 'catalog-fallback'
        );
        console.log(
          ' Featured products source:',
          featuredResponse?.source || 'catalog-filter-isFeatured'
        );
        console.log(' Trending products count:', trending.length);
        console.log(' Featured products count:', featured.length);
        console.log(' Hot New products count:', hotNew.length);
        console.log(' Sale products count:', sale.length);

        setTrendingProducts(trending);
        setFeaturedProducts(featured);
        setSaleProducts(sale);
        setNewArrivalProducts(hotNew);
        writeHomeCache(HOME_PRODUCTS_CACHE_KEY, {
          trendingProducts: trending,
          featuredProducts: featured,
          saleProducts: sale,
          newArrivalProducts: hotNew,
        });

        console.log(` Loaded ${trending.length} trending products`);
        console.log(` Loaded ${sale.length} sale products`);
        console.log(` Loaded ${hotNew.length} hot new products`);
        
      } catch (error) {
        console.error(' Error loading homepage products:', error);
        if (!hasCache) {
          setHomepageError('Failed to load homepage products from backend API');
          setTrendingProducts([]);
          setFeaturedProducts([]);
          setSaleProducts([]);
          setNewArrivalProducts([]);
        }
      } finally {
        if (!cacheFresh || !hasCache) {
          setHomepageLoading(false);
        }
      }
    };

    loadHomepageProducts();
  }, []);

  // ✅ Use real-time categories for hero banner
  const [heroCategories, setHeroCategories] = useState([]);
  const [heroLoading, setHeroLoading] = useState(true);
  // ✅ Use real-time categories for showcase section
  const [showcaseCategories, setShowcaseCategories] = useState([]);
  /** Depth-1 categories: shown below Trending / Hot New, above Sale */
  const [showcaseSubcategories, setShowcaseSubcategories] = useState([]);
  const subcategoriesScrollRef = useRef(null);
  const navigate = useNavigate();

  const readHomeCache = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const writeHomeCache = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
    } catch {
      // Ignore storage failures.
    }
  };

  const scrollSubcategories = useCallback((direction) => {
    const el = subcategoriesScrollRef.current;
    if (!el) return;
    const delta = Math.max(300, Math.floor(el.clientWidth * 0.75));
    el.scrollBy({ left: direction * delta, behavior: 'smooth' });
  }, []);
  
  // Load main categories for hero banner and showcase
  useEffect(() => {
    const loadHeroCategories = async () => {
      const cached = readHomeCache(HOME_CATEGORIES_CACHE_KEY);
      const hasCache =
        cached &&
        Array.isArray(cached?.data?.heroCategories) &&
        Array.isArray(cached?.data?.showcaseCategories) &&
        Array.isArray(cached?.data?.showcaseSubcategories) &&
        cached.data.heroCategories.length > 0;
      const cacheFresh = hasCache && (Date.now() - (cached.ts || 0) < HOME_CACHE_TTL_MS);

      try {
        if (hasCache) {
          setHeroCategories(cached.data.heroCategories);
          setShowcaseCategories(cached.data.showcaseCategories);
          setShowcaseSubcategories(cached.data.showcaseSubcategories);
          setHeroLoading(false);
        } else {
          setHeroLoading(true);
        }
        
        console.log('🔄 Loading categories for hero banner...');
        
        // Fetch categories from API
        const categoriesResponse = await categoryService.getCategories();
        
        let categoriesData = [];
        
        if (categoriesResponse.success && categoriesResponse.categories) {
          if (Array.isArray(categoriesResponse.categories)) {
            categoriesData = categoriesResponse.categories;
          } else if (Array.isArray(categoriesResponse.categories.data)) {
            categoriesData = categoriesResponse.categories.data;
          }
        } else if (Array.isArray(categoriesResponse)) {
          categoriesData = categoriesResponse;
        }
        
        console.log('📦 Categories loaded:', categoriesData.length);

        const sortHomeCategories = (a, b) => {
          const orderA = a.order ?? a.sortOrder ?? 0;
          const orderB = b.order ?? b.sortOrder ?? 0;
          if (orderA !== orderB) return orderA - orderB;
          return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
        };

        // Main only (depth 0) — top CATEGORIES grid + hero; children (depth 2+) stay on /categories only.
        // Fixed display order (Women → Men → Kids → Accessories → Fragrances → Shoes) overrides legacy
        // backend `order` for top-level categories so hero banner and showcase grid stay consistent
        // with the header / mobile drawer.
        const mainCategoriesList = sortMainCategoriesForDisplay(
          categoriesData.filter((category) => {
            if (category.isActive === false) return false;
            return getCategoryDepth(category, categoriesData) === 0;
          })
        );

        // Subcategories only (depth 1) — block between Hot New and Sale
        const subCategoriesList = categoriesData
          .filter((category) => {
            if (category.isActive === false) return false;
            return getCategoryDepth(category, categoriesData) === 1;
          })
          .sort(sortHomeCategories);

        console.log('📦 Main categories:', mainCategoriesList.length, '| Subcategories:', subCategoriesList.length);
        
        // Helper function to create category slug (now returns hierarchical path)
        const getCategorySlugLocal = (category) => {
          return getCategorySlug(category, categoriesData);
        };
          
        const heroCategoriesData = mainCategoriesList.slice(0, 6).map(category => {
          const categorySlug = getCategorySlugLocal(category);
          return {
            id: category._id || category.id,
            name: category.name,
            description: category.description || `Explore our ${category.name} collection`,
            image: category.image || placeholderDataUrl(1200, 600, category.name),
            link: `/category/${categorySlug}`,
            category: category
          };
        });
        
        const showcaseCategoriesData = mainCategoriesList.map(category => {
          const categorySlug = getCategorySlugLocal(category);
          return {
            id: category._id || category.id,
            name: category.name,
            image: category.image || placeholderDataUrl(400, 300, category.name),
            link: `/category/${categorySlug}`,
            category: category
          };
        });

        const showcaseSubcategoriesData = subCategoriesList.map(category => {
          const categorySlug = getCategorySlugLocal(category);
          return {
            id: category._id || category.id,
            name: category.name,
            image: category.image || placeholderDataUrl(400, 300, category.name),
            link: `/category/${categorySlug}`,
            category: category
          };
        });
        
        if (heroCategoriesData.length > 0) {
          console.log('✅ Hero categories set:', heroCategoriesData.length);
          setHeroCategories(heroCategoriesData);
        } else {
          console.warn('⚠️ No main categories for hero, using fallback');
          // Fallback to static categories
          const fallbackCategories = [
            {
              id: 'men-fallback',
              name: 'Men',
              description: 'Explore our latest men\'s collection',
              image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=600&fit=crop',
              link: '/category/men'
            },
            {
              id: 'women-fallback',
              name: 'Women',
              description: 'Explore our latest women\'s collection',
              image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&h=600&fit=crop',
              link: '/category/women'
            }
          ];
          setHeroCategories(fallbackCategories);
        }
        
        setShowcaseCategories(showcaseCategoriesData);
        setShowcaseSubcategories(showcaseSubcategoriesData);
        writeHomeCache(HOME_CATEGORIES_CACHE_KEY, {
          heroCategories: heroCategoriesData,
          showcaseCategories: showcaseCategoriesData,
          showcaseSubcategories: showcaseSubcategoriesData,
        });
        console.log('✅ Showcase mains:', showcaseCategoriesData.length, '| subs:', showcaseSubcategoriesData.length);
      } catch (error) {
        console.error('❌ Error loading hero categories:', error);
        // Fallback to static categories if API fails
        const fallbackCategories = [
          {
            id: 'men-fallback',
            name: 'Men',
            description: 'Explore our latest men\'s collection',
            image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=600&fit=crop',
            link: '/category/men'
          },
          {
            id: 'women-fallback',
            name: 'Women',
            description: 'Explore our latest women\'s collection',
            image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&h=600&fit=crop',
            link: '/category/women'
          }
        ];
        setHeroCategories(fallbackCategories);
        setShowcaseCategories(fallbackCategories);
        setShowcaseSubcategories([]);
      } finally {
        if (!cacheFresh || !hasCache) {
          setHeroLoading(false);
        }
      }
    };
    
    loadHeroCategories();
  }, []);

  // Auto-scroll functionality for hero banner
  useEffect(() => {
    if (heroCategories.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroCategories.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [heroCategories.length]);

  // Load most liked products from wishlist stats
  useEffect(() => {
    const likedProductIds = getMostLikedProducts(8);
    console.log('🔍 Liked product IDs:', likedProductIds);
    console.log('🔍 Available products:', products.length);
    
    const likedProducts = likedProductIds
      .filter(item => item && item.productId)
      .map(({ productId }) => {
        const foundProduct = products.find(p => p.id === productId || p._id === productId);
        console.log('🔍 Looking for productId:', productId, 'Found:', foundProduct);
        console.log('🔍 Found product type:', typeof foundProduct);
        console.log('🔍 Found product keys:', foundProduct ? Object.keys(foundProduct) : 'null');
        return foundProduct || null;
      })
      .filter(product => {
        const isValid = product !== null && typeof product === 'object' && product.id;
        console.log('🔍 Product validation:', product, 'isValid:', isValid);
        return isValid;
      });
    
    console.log('🔍 Final liked products:', likedProducts);
    setDynamicMostLikedProducts(likedProducts);
    
    // Reset currentLikedSlideMan if it's out of bounds
    if (currentLikedSlideMan >= likedProducts.length && likedProducts.length > 0) {
      setCurrentLikedSlideMan(0);
    }
  }, [getMostLikedProducts, products, currentLikedSlideMan]);

  // Handle window resize for group sizes (1 vs 4 columns at 768px).
  // Mobile browsers fire `resize` when the URL bar shows/hides during vertical scroll; innerWidth is unchanged.
  // Only reset carousel indices when the group size actually changes — otherwise auto-slide jumps back to product 1.
  useEffect(() => {
    let lastSize = getGroupSize();
    const handleResize = () => {
      const newSize = getGroupSize();
      setFeaturedGroupSize(newSize);
      setTrendingGroupSize(newSize);
      setHotNewGroupSize(newSize);
      setSaleGroupSize(newSize);
      if (newSize !== lastSize) {
        lastSize = newSize;
        setCurrentFeaturedGroup(0);
        setCurrentTrendingGroup(0);
        setCurrentHotNewGroup(0);
        setCurrentSaleGroup(0);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-scroll featured products every 6 seconds
  useEffect(() => {
    const list = Array.isArray(featuredProducts) ? featuredProducts : [];
    if (list.length === 0) return;
    const validProducts = list.filter((p) => p && (p.id || p._id) && p.name);
    const maxProducts = HOME_SECTION_PRODUCT_LIMIT;
    const totalGroups = Math.ceil(
      Math.min(validProducts.length, maxProducts) / featuredGroupSize
    );
    if (totalGroups <= 1) return;

    const featuredInterval = setInterval(() => {
      if (homeCarouselAutoPausedRef.current) return;
      setCurrentFeaturedGroup((prev) => (prev + 1) % totalGroups);
    }, 6000);

    return () => clearInterval(featuredInterval);
  }, [featuredProducts, featuredGroupSize]);

  // Auto-scroll trending products every 6 seconds (infinite loop)
  useEffect(() => {
    const list = Array.isArray(trendingProducts) ? trendingProducts : [];
    if (list.length === 0) return;
    const validProducts = list.filter(p => p && (p.id || p._id) && p.name);
    const maxProducts = 8;
    const totalTrendingGroups = Math.ceil(Math.min(validProducts.length, maxProducts) / trendingGroupSize);
    if (totalTrendingGroups <= 1) return;

    const trendingInterval = setInterval(() => {
      if (homeCarouselAutoPausedRef.current) return;
      setCurrentTrendingGroup((prev) => (prev + 1) % totalTrendingGroups);
    }, 6000);

    return () => clearInterval(trendingInterval);
  }, [trendingProducts, trendingGroupSize]);

  // Auto-scroll HOT NEW products every 6 seconds (infinite loop)
  useEffect(() => {
    const na = Array.isArray(newArrivalProducts) ? newArrivalProducts : [];
    if (na.length === 0) return;
    
    const latest = getLatestProducts();
    const validProducts = latest.filter(p => p && (p.id || p._id) && p.name);
    const maxProducts = 8;
    const totalGroups = Math.ceil(Math.min(validProducts.length, maxProducts) / hotNewGroupSize);
    if (totalGroups <= 1) return;

    const hotNewInterval = setInterval(() => {
      if (homeCarouselAutoPausedRef.current) return;
      // Opposite direction from Trending: cycle backward (RTL feel)
      setCurrentHotNewGroup((prev) => (prev - 1 + totalGroups) % totalGroups);
    }, 6000);

    return () => clearInterval(hotNewInterval);
  }, [newArrivalProducts, hotNewGroupSize]);

  // Auto-scroll SALE products every 6 seconds (infinite loop)
  useEffect(() => {
    const sp = Array.isArray(saleProducts) ? saleProducts : [];
    if (sp.length === 0) return;
    const validProducts = sp.filter(p => p && (p.id || p._id) && p.name);
    const totalSaleGroups = Math.ceil(Math.min(validProducts.length, HOME_SECTION_PRODUCT_LIMIT) / saleGroupSize);
    if (totalSaleGroups <= 1) return;

    const saleInterval = setInterval(() => {
      if (homeCarouselAutoPausedRef.current) return;
      setCurrentSaleGroup(prev => (prev + 1) % totalSaleGroups); // Infinite loop with modulo
    }, 6000);

    return () => clearInterval(saleInterval);
  }, [saleProducts, saleGroupSize]);

  useEffect(() => {
    const el = featuredSliderRef.current;
    if (!el) return undefined;
    const maxProducts = HOME_SECTION_PRODUCT_LIMIT;
    const list = Array.isArray(featuredProducts) ? featuredProducts : [];
    const totalGroups =
      Math.ceil(Math.min(list.length, maxProducts) / featuredGroupSize) || 1;
    return bindHomeCarouselSwipe(el, featuredSwipeRef, {
      onLeft: () => setCurrentFeaturedGroup((prev) => (prev + 1) % totalGroups),
      onRight: () => setCurrentFeaturedGroup((prev) => (prev - 1 + totalGroups) % totalGroups),
    });
  }, [featuredProducts, featuredGroupSize, homepageLoading]);

  useEffect(() => {
    const el = trendingSliderRef.current;
    if (!el) return undefined;
    const maxProducts = 8;
    const list = Array.isArray(trendingProducts) ? trendingProducts : [];
    const totalGroups = Math.ceil(Math.min(list.length, maxProducts) / trendingGroupSize) || 1;
    return bindHomeCarouselSwipe(el, trendingSwipeRef, {
      onLeft: () => setCurrentTrendingGroup((prev) => (prev + 1) % totalGroups),
      onRight: () => setCurrentTrendingGroup((prev) => (prev - 1 + totalGroups) % totalGroups),
    });
  }, [trendingProducts, trendingGroupSize, homepageLoading]);

  useEffect(() => {
    const el = hotNewSliderRef.current;
    if (!el) return undefined;
    const latest = getLatestProducts();
    const maxProducts = 8;
    const totalGroups =
      latest.length > 0 ? Math.ceil(Math.min(latest.length, maxProducts) / hotNewGroupSize) : 0;
    if (totalGroups <= 0) return undefined;
    return bindHomeCarouselSwipe(el, hotNewSwipeRef, {
      onLeft: () => setCurrentHotNewGroup((prev) => (prev - 1 + totalGroups) % totalGroups),
      onRight: () => setCurrentHotNewGroup((prev) => (prev + 1) % totalGroups),
    });
  }, [newArrivalProducts, hotNewGroupSize, homepageLoading]);

  useEffect(() => {
    const el = saleSliderRef.current;
    if (!el) return undefined;
    const sp = Array.isArray(saleProducts) ? saleProducts : [];
    const groups = Math.ceil(Math.min(sp.length, HOME_SECTION_PRODUCT_LIMIT) / saleGroupSize);
    if (groups <= 0) return undefined;
    return bindHomeCarouselSwipe(el, saleSwipeRef, {
      onLeft: () => setCurrentSaleGroup((prev) => (prev + 1) % groups),
      onRight: () => setCurrentSaleGroup((prev) => (prev - 1 + groups) % groups),
    });
  }, [saleProducts, saleGroupSize, homepageLoading]);

  useEffect(() => {
    const minSwipeDistance = 50;
    const opts = { passive: true };
    const el = heroSliderRef.current;
    if (!el || heroCategories.length === 0) return undefined;
    const onStart = (e) => {
      const t = e.targetTouches[0];
      heroSwipeRef.current = { x: t.clientX, y: t.clientY };
    };
    const onEnd = (e) => {
      const start = heroSwipeRef.current;
      heroSwipeRef.current = null;
      if (!start || !e.changedTouches?.length) return;
      const end = e.changedTouches[0];
      const horizontalDistance = start.x - end.clientX;
      const verticalDistance = start.y - end.clientY;
      if (Math.abs(horizontalDistance) <= Math.abs(verticalDistance)) return;
      const isLeftSwipe = horizontalDistance > minSwipeDistance;
      const isRightSwipe = horizontalDistance < -minSwipeDistance;
      const len = heroCategories.length;
      if (isLeftSwipe) setCurrentSlide((prev) => (prev + 1) % len);
      if (isRightSwipe) setCurrentSlide((prev) => (prev - 1 + len) % len);
    };
    const onCancel = () => {
      heroSwipeRef.current = null;
    };
    el.addEventListener('touchstart', onStart, opts);
    el.addEventListener('touchend', onEnd, opts);
    el.addEventListener('touchcancel', onCancel, opts);
    return () => {
      el.removeEventListener('touchstart', onStart, opts);
      el.removeEventListener('touchend', onEnd, opts);
      el.removeEventListener('touchcancel', onCancel, opts);
    };
  }, [heroCategories, heroLoading]);

  // Show error if there's an error
  if (error) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: '5px',
        margin: '20px',
        color: '#721c24'
      }}>
        <h2> Error Loading Products</h2>
        <p><strong>Error:</strong> {typeof error === 'string' ? error : error?.message || 'Unknown error'}</p>
        <button 
          onClick={() => window.location.reload()} 
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginTop: '15px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }
  
  // Show loading state
  if (loading) {
    return (
      <SiteLoading
        title="Loading..."
        subtitle="Fetching products..."
        icon="🛍️"
        variant="full"
      />
    );
  }
  
  // Show loading state for homepage products
  if (homepageLoading) {
    return (
      <SiteLoading
        title="Loading Homepage"
        subtitle="Loading featured products..."
        icon="✨"
        variant="full"
      />
    );
  }

  // Show error state for homepage products
  if (homepageError) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: '5px',
        margin: '20px',
        color: '#721c24'
      }}>
        <h2> Homepage Products Error</h2>
        <p>{typeof homepageError === 'string' ? homepageError : homepageError?.message || 'Unknown error'}</p>
        <button 
          onClick={() => window.location.reload()} 
          style={{
            padding: '10px 20px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginTop: '15px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const featuredList = Array.isArray(featuredProducts) ? featuredProducts : [];
  const trendingList = Array.isArray(trendingProducts) ? trendingProducts : [];

  const getHomeCarouselSlice = (list, groupSize, currentGroup, maxProducts = HOME_SECTION_PRODUCT_LIMIT) => {
    if (!list.length) return [];
    const limitedProducts = list
      .filter((p) => p && (p.id || p._id) && p.name)
      .slice(0, maxProducts);
    if (!limitedProducts.length) return [];
    const totalGroups = Math.ceil(limitedProducts.length / groupSize);
    const normalizedGroup = currentGroup % totalGroups;
    const startIndex = normalizedGroup * groupSize;
    return limitedProducts
      .slice(startIndex, startIndex + groupSize)
      .filter((p) => p && (p.id || p._id));
  };

  const getCurrentFeaturedProducts = () =>
    getHomeCarouselSlice(featuredList, featuredGroupSize, currentFeaturedGroup);

  //  FIXED: Get current trending products group using main products API (max 8 products)
  const getCurrentTrendingProducts = () => {
    if (trendingList.length === 0) return [];
    const maxProducts = 8;
    const limitedProducts = trendingList
      .filter(p => p && (p.id || p._id) && p.name) // Filter out empty/invalid products
      .slice(0, maxProducts);
    
    if (limitedProducts.length === 0) return [];
    
    // Infinite loop: wrap around if we exceed the array
    const totalGroups = Math.ceil(limitedProducts.length / trendingGroupSize);
    const normalizedGroup = currentTrendingGroup % totalGroups;
    const startIndex = normalizedGroup * trendingGroupSize;
    return limitedProducts.slice(startIndex, startIndex + trendingGroupSize).filter(p => p && (p.id || p._id));
  };

  // Trending navigation
  const goToTrendingGroup = (index) => {
    setCurrentTrendingGroup(index);
  };

  const showHomeCarouselMobileArrows = (groupSize, totalGroups) =>
    groupSize === 1 && totalGroups > 1;

  const totalFeaturedGroups =
    Math.ceil(Math.min(featuredList.length, HOME_SECTION_PRODUCT_LIMIT) / featuredGroupSize) ||
    1;
  const showFeaturedMobileArrows = showHomeCarouselMobileArrows(
    featuredGroupSize,
    totalFeaturedGroups
  );

  const goToFeaturedGroup = (index) => {
    setCurrentFeaturedGroup(index);
  };

  const goToPrevFeaturedGroup = () => {
    setCurrentFeaturedGroup(
      (prev) => (prev - 1 + totalFeaturedGroups) % totalFeaturedGroups
    );
  };

  const goToNextFeaturedGroup = () => {
    setCurrentFeaturedGroup((prev) => (prev + 1) % totalFeaturedGroups);
  };

  const totalTrendingGroups =
    Math.ceil(Math.min(trendingList.length, 8) / trendingGroupSize) || 1;
  const showTrendingMobileArrows = showHomeCarouselMobileArrows(
    trendingGroupSize,
    totalTrendingGroups
  );

  const goToPrevTrendingGroup = () => {
    setCurrentTrendingGroup(
      (prev) => (prev - 1 + totalTrendingGroups) % totalTrendingGroups
    );
  };

  const goToNextTrendingGroup = () => {
    setCurrentTrendingGroup((prev) => (prev + 1) % totalTrendingGroups);
  };

  const latestProducts = getLatestProducts();
  
  // HOT NEW section - Latest products by date
  const getTotalHotNewGroups = () => {
    if (!latestProducts || latestProducts.length === 0) return 0;
    const maxProducts = 8;
    return Math.ceil(Math.min(latestProducts.length, maxProducts) / hotNewGroupSize);
  };
  
  const totalHotNewGroups = getTotalHotNewGroups();

  const getCurrentHotNewProducts = () => {
    if (!latestProducts || latestProducts.length === 0) return [];
    const maxProducts = 8;
    const limitedProducts = latestProducts
      .filter(p => p && (p.id || p._id) && p.name) // Filter out empty/invalid products
      .slice(0, maxProducts);
    
    if (limitedProducts.length === 0) return [];
    
    // Infinite loop: wrap around if we exceed the array
    const totalGroups = Math.ceil(limitedProducts.length / hotNewGroupSize);
    const normalizedGroup = currentHotNewGroup % totalGroups;
    const startIndex = normalizedGroup * hotNewGroupSize;
    return limitedProducts.slice(startIndex, startIndex + hotNewGroupSize).filter(p => p && (p.id || p._id));
  };

  const goToHotNewGroup = (index) => {
    setCurrentHotNewGroup(index);
  };

  const showHotNewMobileArrows = showHomeCarouselMobileArrows(
    hotNewGroupSize,
    totalHotNewGroups
  );

  const goToPrevHotNewGroup = () => {
    setCurrentHotNewGroup(
      (prev) => (prev - 1 + totalHotNewGroups) % totalHotNewGroups
    );
  };

  const goToNextHotNewGroup = () => {
    setCurrentHotNewGroup((prev) => (prev + 1) % totalHotNewGroups);
  };

  // SALE section - Sale products carousel
  const saleList = Array.isArray(saleProducts) ? saleProducts : [];
  const totalSaleGroups = Math.ceil(Math.min(saleList.length, HOME_SECTION_PRODUCT_LIMIT) / saleGroupSize);

  const getCurrentSaleProducts = () => {
    if (saleList.length === 0) return [];
    const limitedProducts = saleList
      .filter(p => p && (p.id || p._id) && p.name)
      .slice(0, HOME_SECTION_PRODUCT_LIMIT);
    
    if (limitedProducts.length === 0) return [];
    
    const totalGroups = Math.ceil(limitedProducts.length / saleGroupSize);
    const normalizedGroup = currentSaleGroup % totalGroups;
    const startIndex = normalizedGroup * saleGroupSize;
    return limitedProducts.slice(startIndex, startIndex + saleGroupSize).filter(p => p && (p.id || p._id));
  };

  const goToSaleGroup = (index) => {
    setCurrentSaleGroup(index);
  };

  const showSaleMobileArrows = showHomeCarouselMobileArrows(
    saleGroupSize,
    totalSaleGroups
  );

  const goToPrevSaleGroup = () => {
    setCurrentSaleGroup((prev) => (prev - 1 + totalSaleGroups) % totalSaleGroups);
  };

  const goToNextSaleGroup = () => {
    setCurrentSaleGroup((prev) => (prev + 1) % totalSaleGroups);
  };

  // Real-time products for Man category
  const productList = Array.isArray(products) ? products : [];
  const manProducts = productList.filter(p => p.category === 'Man');
  const mostViewedManProducts = [...manProducts].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 4);
  const bestSellingManProducts = [...manProducts].sort((a, b) => (b.sold || 0) - (a.sold || 0)).slice(0, 4);
  const mostLikedManProducts = [...manProducts].sort((a, b) => (b.likes || 0) - (a.likes || 0)).slice(0, 4);

  const showExploreMostViewed = mostViewedManProducts.length > 0;
  const showExploreBestSelling = bestSellingManProducts.length > 0;
  const showExploreMostLiked = dynamicMostLikedProducts.length > 0;
  const exploreVisibleCount = [showExploreMostViewed, showExploreBestSelling, showExploreMostLiked].filter(Boolean)
    .length;
  const hasMoreExploreSection = exploreVisibleCount > 0;

  return (
    <div className="home">
      {/* Hero Banner with Real-time Categories */}
      <section className="hero-banner">
        {heroLoading ? (
          <div className="hero-loading">
            <SiteLoading
              title="Loading categories..."
              variant="inline"
            />
          </div>
        ) : heroCategories.length === 0 ? (
          <div className="hero-loading">
            <SiteLoading
              title="No categories available"
              variant="inline"
            />
          </div>
        ) : (
          <>
            <div className="hero-slider" ref={heroSliderRef}>
              {heroCategories.map((category, index) => (
                <div
                  key={category.id}
                  className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
                  style={{ 
                    backgroundImage: `url(${category.image})`,
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate(category.link)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(category.link);
                    }
                  }}
                >
                  <div className="hero-content">
                    <div className="container">
                      <div className="hero-text">
                        <h1 className="hero-title">{category.name}</h1>
                        <p className="hero-subtitle">{category.description}</p>
                        <Link 
                          to={category.link} 
                          className="btn btn-primary hero-btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Explore Collection
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="hero-overlay"></div>
                </div>
              ))}
            </div>
            {/* Slide Indicators */}
            <div className="hero-indicators">
              {heroCategories.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                ></button>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Featured Products — admin isFeatured flag (clone of Trending carousel) */}
      {featuredList.length > 0 && (
        <section className="featured-products featured-products--featured section">
          <div className="container">
            <h2 className="section-title">FEATURED</h2>

            <div
              className={`trending-carousel${showFeaturedMobileArrows ? ' trending-carousel--with-arrows' : ''}`}
            >
              {showFeaturedMobileArrows && (
                <>
                  <button
                    type="button"
                    className="home-carousel-arrow home-carousel-arrow--prev"
                    aria-label="Previous featured product"
                    onClick={goToPrevFeaturedGroup}
                  >
                    <i className="fas fa-chevron-left" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="home-carousel-arrow home-carousel-arrow--next"
                    aria-label="Next featured product"
                    onClick={goToNextFeaturedGroup}
                  >
                    <i className="fas fa-chevron-right" aria-hidden />
                  </button>
                </>
              )}
              <div className="trending-slider trending-slider--featured" ref={featuredSliderRef}>
                <div
                  className="trending-grid trending-grid--slide-ltr"
                  key={homeCarouselGridKey(featuredGroupSize, currentFeaturedGroup)}
                >
                  {getCurrentFeaturedProducts().map((product) => (
                    <ProductCard
                      key={product.id || product._id}
                      product={product}
                      variant="homeCarousel"
                    />
                  ))}
                </div>
              </div>

              {totalFeaturedGroups > 1 && (
                <div className="trending-indicators">
                  {Array.from({ length: totalFeaturedGroups }).map((_, index) => (
                    <button
                      key={index}
                      className={`trending-indicator ${index === currentFeaturedGroup ? 'active' : ''}`}
                      onClick={() => goToFeaturedGroup(index)}
                      aria-label={`Featured products slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Categories Section */}
      <section className="categories-showcase section">
        <div className="categories-showcase-container">
          <div className="categories-showcase-header">
            <h2 className="categories-showcase-title">CATEGORIES</h2>
            <p className="categories-showcase-subtitle">Explore our complete collection</p>
          </div>
          
          <div className="categories-showcase-content">
            <div className="categories-grid">
              {showcaseCategories.length > 0 ? (
                showcaseCategories.map(category => (
                <Link 
                  key={category.id} 
                  to={category.link} 
                  className="category-showcase-card"
                >
                  <div className="category-image">
                    <img 
                      src={category.image} 
                      alt={category.name}
                        onError={(e) => {
                          e.target.src = placeholderDataUrl(400, 300, category.name);
                        }}
                    />
                    <div className="category-bottom-overlay">
                      <span className="category-name">{category.name}</span>
                      <span className="explore-btn">Explore</span>
                    </div>
                  </div>
                </Link>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', gridColumn: '1 / -1' }}>
                  <p>Loading categories...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products - TRENDING */}
      <section className="featured-products featured-products--trending section">
        <div className="container">
          <h2 className="section-title">TRENDING</h2>
          
          <div
            className={`trending-carousel${showTrendingMobileArrows ? ' trending-carousel--with-arrows' : ''}`}
          >
            {showTrendingMobileArrows && (
              <>
                <button
                  type="button"
                  className="home-carousel-arrow home-carousel-arrow--prev"
                  aria-label="Previous trending product"
                  onClick={goToPrevTrendingGroup}
                >
                  <i className="fas fa-chevron-left" aria-hidden />
                </button>
                <button
                  type="button"
                  className="home-carousel-arrow home-carousel-arrow--next"
                  aria-label="Next trending product"
                  onClick={goToNextTrendingGroup}
                >
                  <i className="fas fa-chevron-right" aria-hidden />
                </button>
              </>
            )}
            <div className="trending-slider trending-slider--trending" ref={trendingSliderRef}>
              <div
                className="trending-grid trending-grid--slide-ltr"
                key={homeCarouselGridKey(trendingGroupSize, currentTrendingGroup)}
              >
                {getCurrentTrendingProducts()
                  .filter((product) => product && (product.id || product._id) && product.name)
                  .map((product) => (
                    <ProductCard key={product.id || product._id} product={product} variant="homeCarousel" />
                  ))}
              </div>
            </div>

            {/* Trending Indicators */}
            {Math.ceil(Math.min(trendingList.length, 8) / trendingGroupSize) > 1 && (
            <div className="trending-indicators">
                {Array.from({ length: Math.ceil(Math.min(trendingList.length, 8) / trendingGroupSize) }).map((_, index) => (
                <button
                  key={index}
                  className={`trending-indicator ${index === currentTrendingGroup ? 'active' : ''}`}
                  onClick={() => goToTrendingGroup(index)}
                />
              ))}
            </div>
            )}
          </div>
        </div>
      </section>

      {/* HOT NEW Products */}
      <section className="featured-products featured-products--hot-new section">
        <div className="container">
          <h2 className="section-title">HOT NEW</h2>
          
          <div
            className={`trending-carousel${showHotNewMobileArrows ? ' trending-carousel--with-arrows' : ''}`}
          >
            {showHotNewMobileArrows && (
              <>
                <button
                  type="button"
                  className="home-carousel-arrow home-carousel-arrow--prev"
                  aria-label="Previous hot new product"
                  onClick={goToPrevHotNewGroup}
                >
                  <i className="fas fa-chevron-left" aria-hidden />
                </button>
                <button
                  type="button"
                  className="home-carousel-arrow home-carousel-arrow--next"
                  aria-label="Next hot new product"
                  onClick={goToNextHotNewGroup}
                >
                  <i className="fas fa-chevron-right" aria-hidden />
                </button>
              </>
            )}
            <div className="trending-slider trending-slider--hot-new" ref={hotNewSliderRef}>
              <div
                className="trending-grid trending-grid--slide-rtl"
                key={homeCarouselGridKey(hotNewGroupSize, currentHotNewGroup)}
              >
                {getCurrentHotNewProducts()
                  .filter(product => product && (product.id || product._id) && product.name)
                  .map(product => (
                  <ProductCard key={product.id || product._id} product={product} variant="homeCarousel" />
                ))}
              </div>
            </div>

            {/* HOT NEW Indicators */}
            {totalHotNewGroups > 1 && (
            <div className="trending-indicators">
                {Array.from({ length: totalHotNewGroups }).map((_, index) => (
                <button
                  key={index}
                  className={`trending-indicator ${index === currentHotNewGroup ? 'active' : ''}`}
                  onClick={() => goToHotNewGroup(index)}
                />
              ))}
            </div>
            )}
          </div>

          <div className="text-center mt-3">
            <Link to="/category/new-in" className="btn btn-secondary">
              View All New Arrivals
            </Link>
          </div>
        </div>
      </section>

      {/* Subcategories + View all categories — between Hot New and Sale */}
      <section className="categories-showcase section home-subcategories-section">
        <div className="categories-showcase-container">
          <div className="categories-showcase-header">
            <h2 className="categories-showcase-title">SUBCATEGORIES</h2>
            <p className="categories-showcase-subtitle">Browse collections under each main category</p>
          </div>
          <div className="categories-showcase-content">
            <div
              className={`home-subcategories-carousel-wrap${showcaseSubcategories.length <= 1 ? ' home-subcategories-carousel-wrap--single' : ''}`}
            >
              <button
                type="button"
                className="home-subcategories-arrow home-subcategories-arrow--prev"
                onClick={() => scrollSubcategories(-1)}
                aria-label="Scroll subcategories left"
              >
                <i className="fas fa-chevron-left" aria-hidden />
              </button>
              <div className="home-subcategories-scroll" ref={subcategoriesScrollRef}>
                <div className="home-subcategories-row">
                  {showcaseSubcategories.map((category) => (
                    <Link
                      key={category.id}
                      to={category.link}
                      className="category-showcase-card home-subcategory-card"
                    >
                      <div className="category-image">
                        <img
                          src={category.image}
                          alt={category.name}
                          onError={(e) => {
                            e.target.src = placeholderDataUrl(400, 300, category.name);
                          }}
                        />
                        <div className="category-bottom-overlay">
                          <span className="category-name">{category.name}</span>
                          <span className="explore-btn">Explore</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
              <button
                type="button"
                className="home-subcategories-arrow home-subcategories-arrow--next"
                onClick={() => scrollSubcategories(1)}
                aria-label="Scroll subcategories right"
              >
                <i className="fas fa-chevron-right" aria-hidden />
              </button>
            </div>
            <div className="categories-show-more">
              <Link to="/categories" className="btn-show-more">
                <span>View All Categories</span>
                <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SALE Products Section — only products with an active sale */}
      {saleList.length > 0 && (
      <section className="sale-products sale-products--home-carousel section">
        <div className="container">
          <h2 className="section-title">SALE</h2>
          
          <div
            className={`trending-carousel${showSaleMobileArrows ? ' trending-carousel--with-arrows' : ''}`}
          >
            {showSaleMobileArrows && (
              <>
                <button
                  type="button"
                  className="home-carousel-arrow home-carousel-arrow--prev"
                  aria-label="Previous sale product"
                  onClick={goToPrevSaleGroup}
                >
                  <i className="fas fa-chevron-left" aria-hidden />
                </button>
                <button
                  type="button"
                  className="home-carousel-arrow home-carousel-arrow--next"
                  aria-label="Next sale product"
                  onClick={goToNextSaleGroup}
                >
                  <i className="fas fa-chevron-right" aria-hidden />
                </button>
              </>
            )}
            <div className="trending-slider trending-slider--sale" ref={saleSliderRef}>
              <div
                className="trending-grid trending-grid--slide-ltr"
                key={homeCarouselGridKey(saleGroupSize, currentSaleGroup)}
              >
                {getCurrentSaleProducts()
                  .filter(product => product && (product.id || product._id) && product.name)
                  .map(product => (
                  <ProductCard key={product.id || product._id} product={product} variant="homeCarousel" />
                ))}
              </div>
            </div>

            {totalSaleGroups > 1 && (
            <div className="trending-indicators">
                {Array.from({ length: totalSaleGroups }).map((_, index) => (
                <button
                  key={index}
                  className={`trending-indicator ${index === currentSaleGroup ? 'active' : ''}`}
                  onClick={() => goToSaleGroup(index)}
                  aria-label={`Sale slide ${index + 1}`}
                />
              ))}
            </div>
            )}
          </div>

          <div className="text-center mt-3">
            <Link to="/category/sale" className="btn btn-secondary">
              View All Sale Items
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* More to Explore — heading always visible; cards only when at least one column has products */}
      <section className="more-explore section">
        <div className="container">
          <Link to="/category/all" className="more-explore-title-link" title="View all products">
            <h2 className="section-title">MORE TO EXPLORE</h2>
          </Link>
          {hasMoreExploreSection && (
          <div
            className={`explore-grid grid grid-3 explore-grid--cols-${exploreVisibleCount}`}
          >
            {/* Most Viewed Products */}
            {showExploreMostViewed && (
            <div className="explore-item">
              <div className="explore-header">
                <h3>Most Viewed</h3>
                <p>Popular Among Customers</p>
              </div>
              <div className="explore-content">
                <div className="explore-slider">
                  <div className="explore-product active">
                    {mostViewedManProducts[currentViewedSlideMan] && (
                    <Link to={`/product/${mostViewedManProducts[currentViewedSlideMan].id}`} className="explore-product-card">
                      <img src={mostViewedManProducts[currentViewedSlideMan].image} alt={mostViewedManProducts[currentViewedSlideMan].name} className="explore-card-image" />
                      <div className="explore-overlay"></div>
                      <div className="explore-text-container">
                        <h4 className="explore-product-name">{mostViewedManProducts[currentViewedSlideMan].name}</h4>
                        <span className="explore-product-category">{mostViewedManProducts[currentViewedSlideMan].category}</span>
                        <div className="explore-product-stats">
                          <span className="explore-price">Rs.{mostViewedManProducts[currentViewedSlideMan].price}</span>
                        </div>
                      </div>
                    </Link>
                    )}
                  </div>
                </div>
                <div className="explore-indicators">
                  {mostViewedManProducts.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`explore-indicator ${index === currentViewedSlideMan ? 'active' : ''}`}
                      onClick={() => setCurrentViewedSlideMan(index)}
                    />
                  ))}
                </div>
              </div>
            </div>
            )}

            {/* Best Selling Products */}
            {showExploreBestSelling && (
            <div className="explore-item">
              <div className="explore-header">
                <h3>Best Selling</h3>
                <p>Top Customer Choices</p>
              </div>
              <div className="explore-content">
                <div className="explore-slider">
                  <div className="explore-product active">
                    {bestSellingManProducts[currentSellingSlideMan] && (
                    <Link to={`/product/${bestSellingManProducts[currentSellingSlideMan].id}`} className="explore-product-card">
                      <img src={bestSellingManProducts[currentSellingSlideMan].image} alt={bestSellingManProducts[currentSellingSlideMan].name} className="explore-card-image" />
                      <div className="explore-overlay"></div>
                      <div className="explore-text-container">
                        <h4 className="explore-product-name">{bestSellingManProducts[currentSellingSlideMan].name}</h4>
                        <span className="explore-product-category">{bestSellingManProducts[currentSellingSlideMan].category}</span>
                        <div className="explore-product-stats">
                          <span className="explore-price">Rs.{bestSellingManProducts[currentSellingSlideMan].price}</span>
                        </div>
                      </div>
                    </Link>
                    )}
                  </div>
                </div>
                <div className="explore-indicators">
                  {bestSellingManProducts.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`explore-indicator ${index === currentSellingSlideMan ? 'active' : ''}`}
                      onClick={() => setCurrentSellingSlideMan(index)}
                    />
                  ))}
                </div>
              </div>
            </div>
            )}

            {/* Most Liked Products */}
            {showExploreMostLiked && (
            <div className="explore-item">
              <div className="explore-header">
                <h3>Most Liked</h3>
                <p>Customer Favorites</p>
              </div>
              <div className="explore-content">
                <div className="explore-slider">
                  <div className="explore-product active">
                    {dynamicMostLikedProducts[currentLikedSlideMan] && (
                      <Link to={`/${dynamicMostLikedProducts[currentLikedSlideMan]?.url || `product/${dynamicMostLikedProducts[currentLikedSlideMan]?.id}`}`} className="explore-product-card">
                        <img
                          src={
                            getPrimaryProductImageUrl(dynamicMostLikedProducts[currentLikedSlideMan]) ||
                            dynamicMostLikedProducts[currentLikedSlideMan]?.image
                          }
                          alt={dynamicMostLikedProducts[currentLikedSlideMan]?.name}
                          className="explore-card-image"
                        />
                        <div className="explore-overlay"></div>
                        <div className="explore-text-container">
                          <h4 className="explore-product-name">{dynamicMostLikedProducts[currentLikedSlideMan]?.name}</h4>
                          <span className="explore-product-category">
                            {typeof dynamicMostLikedProducts[currentLikedSlideMan]?.category === 'string' 
                              ? dynamicMostLikedProducts[currentLikedSlideMan]?.category 
                              : dynamicMostLikedProducts[currentLikedSlideMan]?.category?.name || 'General'}
                          </span>
                          <div className="explore-product-stats">
                            <span className="explore-price">Rs.{dynamicMostLikedProducts[currentLikedSlideMan]?.price?.toLocaleString()}</span>
                          </div>
                        </div>
                      </Link>
                    )}
                  </div>
                </div>
                <div className="explore-indicators">
                  {dynamicMostLikedProducts.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`explore-indicator ${index === currentLikedSlideMan ? 'active' : ''}`}
                      onClick={() => setCurrentLikedSlideMan(index)}
                    />
                  ))}
                </div>
              </div>
            </div>
            )}
          </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
